// src/components/HelpDrawer.jsx
//
// The nav's "?" button and the panel it opens: help for the page you are
// currently on, resolved from the route (src/help/pageHelp.js). Deliberately
// route-scoped rather than one big manual — the question a user actually has is
// "what does THIS screen do", asked while looking at it.
//
// MUI (Drawer/Chip/Button) rather than the plain-inline-style idiom used by
// MainApp's own panels, because this mounts inside App.js's <nav>, which is
// already MUI (Menu/MenuItem/Badge) under the shared ThemeProvider.
//
// Renders nothing but the button when the current route has no entry, so an
// unhelped page degrades to a missing button rather than an empty drawer.

import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { getHelpForPath } from '../help/pageHelp';

const NUM_SX = {
  flex: '0 0 auto',
  width: 20,
  height: 20,
  borderRadius: '50%',
  fontSize: 11,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'rgba(79,179,188,0.18)',
  color: '#4fb3bc',
  mt: '1px',
};

export default function HelpDrawer() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const help = getHelpForPath(location.pathname, location.search);

  return (
    <>
      <Tooltip title={help ? `What is ${help.label}?` : 'Help'}>
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label="Help for this page"
          sx={{ color: 'var(--muted)', '&:hover': { color: '#4fb3bc' } }}
        >
          <HelpOutlineOutlinedIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: 440 },
              bgcolor: 'var(--card)',
              backgroundImage: 'none',
              borderLeft: '1px solid var(--border)',
            },
          },
        }}
      >
        <Box sx={{ p: 2.5, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
            <Typography variant="h6" sx={{ flex: 1, fontSize: 17, color: 'var(--ink)' }}>
              {help ? help.label : 'Help'}
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close help">
              <CloseIcon sx={{ fontSize: 18, color: 'var(--muted)' }} />
            </IconButton>
          </Box>

          {!help && (
            <Typography sx={{ fontSize: 13, color: 'var(--muted)' }}>
              No help written for this page yet.{' '}
              <Link to="/help" onClick={() => setOpen(false)} style={{ color: '#4fb3bc' }}>
                See all pages →
              </Link>
            </Typography>
          )}

          {help && (
            <>
              <Chip
                size="small"
                label={help.needsTraffic ? 'Needs a live capture' : 'Works standalone'}
                sx={{
                  height: 20,
                  fontSize: 10.5,
                  fontWeight: 600,
                  mb: 1.5,
                  bgcolor: help.needsTraffic ? 'rgba(245,158,11,0.16)' : 'rgba(34,197,94,0.16)',
                  color: help.needsTraffic ? '#f59e0b' : '#22c55e',
                }}
              />

              <Typography sx={{ fontSize: 13.5, color: 'var(--ink)', mb: 1.5, lineHeight: 1.6 }}>
                {help.what}
              </Typography>

              {help.useWhen && (
                <Box
                  sx={{
                    p: 1.25,
                    mb: 2,
                    borderLeft: '2px solid #4fb3bc',
                    bgcolor: 'rgba(79,179,188,0.07)',
                    borderRadius: '0 4px 4px 0',
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55 }}>
                    <b style={{ color: 'var(--ink)' }}>Use it when: </b>
                    {help.useWhen}
                  </Typography>
                </Box>
              )}

              {help.steps?.length > 0 && (
                <>
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--muted)', mb: 1 }}
                  >
                    HOW TO USE IT
                  </Typography>
                  <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2.5 }}>
                    {help.steps.map((s, i) => (
                      <Box
                        key={i}
                        component="li"
                        sx={{ display: 'flex', gap: 1.25, mb: 1.25, alignItems: 'flex-start' }}
                      >
                        <Box sx={NUM_SX}>{i + 1}</Box>
                        <Typography sx={{ fontSize: 12.5, color: 'var(--ink)', lineHeight: 1.6 }}>
                          {s}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {help.gotchas?.length > 0 && (
                <>
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--muted)', mb: 1 }}
                  >
                    GOTCHAS
                  </Typography>
                  {help.gotchas.map((g, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                      <WarningAmberOutlinedIcon sx={{ fontSize: 15, color: '#f59e0b', mt: '2px' }} />
                      <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6 }}>
                        {g}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

              <Divider sx={{ my: 2, borderColor: 'var(--border)' }} />
              <Link
                to="/help"
                onClick={() => setOpen(false)}
                style={{ color: '#4fb3bc', fontSize: 12.5, textDecoration: 'none' }}
              >
                All pages &amp; "which page do I want?" →
              </Link>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
}
